/**
 * Dialog showing summary of legacy workspace conversion
 */

import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import PropTypes from "prop-types";

const ConversionSummaryDialog = ({
  visible,
  onHide,
  desktopName,
  workspace,
  stats,
  sourceLabel = "WebCharts Antigo",
  itemLabel = "Desktop",
}) => {
  // Early return if no data - prevents errors when dialog is not visible
  if (!workspace || !stats) {
    return null;
  }

  // Group indicators by type
  const indicatorsByType = React.useMemo(() => {
    if (!workspace?.charts) return {};

    const groups = {};
    for (const chart of workspace.charts) {
      if (chart.indicators) {
        for (const indicator of chart.indicators) {
          const type = indicator.key || "unknown";
          if (!groups[type]) {
            groups[type] = 0;
          }
          groups[type]++;
        }
      }
    }
    return groups;
  }, [workspace]);

  // Format indicator type name for display
  const formatIndicatorName = (key) => {
    const names = {
      sma: "Média Móvel Simples (SMA)",
      ema: "Média Móvel Exponencial (EMA)",
      wma: "Média Móvel Ponderada (WMA)",
      bollingerBand: "Bandas de Bollinger",
      volume: "Volume",
      macd: "MACD",
      adx: "ADX",
      rsi: "RSI",
      vwap: "VWAP",
      stochastic: "Estocástico",
      cci: "CCI",
      didi: "DIDI",
      atr: "ATR",
      schaffTrendCycle: "Schaff Trend Cycle",
      onBalanceVolume: "On Balance Volume",
      mfi: "Money Flow Index",
      alligator: "Alligator",
      awesomeOscillator: "Awesome Oscillator",
      pivotPoints: "Pivot Points",
      volumeProfile: "Volume Profile",
      zigzag: "ZigZag",
      supertrend: "Supertrend",
      atrStop: "ATR Trailing Stop",
      williamsad: "Williams A/D",
      highLow: "High Low",
      medianPrice: "Median Price",
      keltner: "Keltner Channel",
      bollingerBandwidth: "Bollinger Bandwidth",
      klinger: "Klinger Oscillator",
    };
    return names[key] || key;
  };

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
      <i
        className="pi pi-check-circle"
        style={{ color: "var(--green-500)" }}
      ></i>
      <span>Conversão Concluída</span>
    </div>
  );

  const footer = (
    <div>
      <Button
        label="OK"
        icon="pi pi-check"
        onClick={onHide}
        autoFocus
        data-evo-id="conversionsummarydialog-btn-ok"
      />
    </div>
  );

  return (
    <Dialog
      header={header}
      visible={visible}
      style={{ width: "min(550px, 92vw)" }}
      onHide={onHide}
      footer={footer}
      modal
    >
      <div className="conversion-summary-dialog" style={{ overflow: "auto" }}>
        {/* Success Message */}
        <Message
          severity="success"
          text={`${itemLabel} "${desktopName}" foi importado com sucesso do ${sourceLabel}!`}
          style={{ width: "100%", marginBottom: "1.5rem" }}
        />

        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          {/* Charts */}
          <div
            style={{
              padding: "1rem",
              background: "var(--surface-card)",
              borderRadius: "6px",
              border: "1px solid var(--surface-border)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "32px",
                fontWeight: "700",
                color: "var(--primary-color)",
                marginBottom: "0.5rem",
              }}
            >
              {stats.charts}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "var(--text-color-secondary)",
              }}
            >
              {stats.charts === 1 ? "Gráfico" : "Gráficos"}
            </div>
          </div>

          {/* Indicators */}
          <div
            style={{
              padding: "1rem",
              background: "var(--surface-card)",
              borderRadius: "6px",
              border: "1px solid var(--surface-border)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "32px",
                fontWeight: "700",
                color: "var(--orange-500)",
                marginBottom: "0.5rem",
              }}
            >
              {stats.indicators}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "var(--text-color-secondary)",
              }}
            >
              {stats.indicators === 1 ? "Indicador" : "Indicadores"}
            </div>
          </div>

          {/* Drawings - Hidden because old HTML5 chart doesn't save objects */}
          {/*
          <div style={{
            padding: '1rem',
            background: 'var(--surface-card)',
            borderRadius: '6px',
            border: '1px solid var(--surface-border)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '700',
              color: 'var(--cyan-500)',
              marginBottom: '0.5rem'
            }}>
              {stats.drawings}
            </div>
            <div style={{
              fontSize: '13px',
              color: 'var(--text-color-secondary)'
            }}>
              {stats.drawings === 1 ? 'Desenho' : 'Desenhos'}
            </div>
          </div>
          */}
        </div>

        {/* Indicator Details */}
        {stats.indicators > 0 && (
          <div
            style={{
              padding: "1rem",
              background: "var(--surface-ground)",
              borderRadius: "6px",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <i className="pi pi-chart-line" style={{ fontSize: "14px" }}></i>
              Indicadores Convertidos
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {Object.entries(indicatorsByType)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <div
                    key={type}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.5rem",
                      background: "var(--surface-card)",
                      borderRadius: "4px",
                      fontSize: "13px",
                    }}
                  >
                    <span style={{ color: "var(--text-color)" }}>
                      {formatIndicatorName(type)}
                    </span>
                    <span
                      style={{
                        background: "var(--orange-100)",
                        color: "var(--orange-700)",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Skipped Indicators Warning */}
        {stats.skippedIndicators && stats.skippedIndicators.length > 0 && (
          <div
            style={{
              padding: "1rem",
              background: "var(--surface-ground)",
              borderRadius: "6px",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                opacity: 0.6,
              }}
            >
              <i className="pi pi-info-circle" style={{ fontSize: "14px" }}></i>
              Não Convertidos
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {stats.skippedIndicators.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.5rem",
                    background: "var(--surface-card)",
                    borderRadius: "4px",
                    fontSize: "13px",
                    opacity: 0.7,
                  }}
                >
                  <span style={{ color: "var(--text-color)" }}>
                    {item.type}
                    {item.reason && (
                      <span
                        style={{
                          display: "block",
                          fontSize: "11px",
                          color: "var(--text-color-secondary)",
                        }}
                      >
                        {item.reason}
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      background: "var(--surface-border)",
                      color: "var(--text-color-secondary)",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "600",
                      flexShrink: 0,
                    }}
                  >
                    N/A
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Failed Indicators Error */}
        {stats.failedIndicators && stats.failedIndicators.length > 0 && (
          <div
            style={{
              padding: "1rem",
              background: "var(--surface-ground)",
              borderRadius: "6px",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                color: "var(--red-500)",
              }}
            >
              <i
                className="pi pi-exclamation-triangle"
                style={{ fontSize: "14px" }}
              ></i>
              Erros na Conversão
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {stats.failedIndicators.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.5rem",
                    background: "var(--surface-card)",
                    borderRadius: "4px",
                    fontSize: "13px",
                  }}
                >
                  <span style={{ color: "var(--text-color)" }}>
                    {item.type}
                  </span>
                  <span
                    style={{
                      background: "var(--red-100)",
                      color: "var(--red-700)",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    ⚠
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Message */}
        <div
          style={{
            padding: "0.75rem 1rem",
            background: "var(--surface-ground)",
            borderLeft: "3px solid var(--primary-color)",
            borderRadius: "4px",
            fontSize: "13px",
            color: "var(--text-color-secondary)",
            lineHeight: "1.5",
            wordWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          {itemLabel === "Template" ? (
            <>
              Os indicadores e o tipo de gráfico foram aplicados ao gráfico
              selecionado.
            </>
          ) : (
            <>
              O desktop foi carregado no workspace atual. Use{" "}
              <strong style={{ color: "var(--text-color)" }}>
                Salvar Desktop
              </strong>{" "}
              para salvá-lo permanentemente.
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

ConversionSummaryDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  desktopName: PropTypes.string.isRequired,
  sourceLabel: PropTypes.string,
  itemLabel: PropTypes.string,
  workspace: PropTypes.object,
  stats: PropTypes.shape({
    charts: PropTypes.number,
    indicators: PropTypes.number,
    drawings: PropTypes.number,
    skippedIndicators: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        reason: PropTypes.string,
      }),
    ),
    failedIndicators: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        error: PropTypes.string,
      }),
    ),
  }),
};

export default ConversionSummaryDialog;
